import { USA_ZIP_CODE_REGEX } from './zipCodeSchemas';

type PrefixRange = [start: number, end: number];

/**
 * USPS 3-digit ZIP prefix ranges per state (inclusive). A prefix may belong to
 * more than one state where USPS assigns it across a border (e.g. 063 covers
 * Fishers Island NY inside the CT block, 05501 is Andover MA inside the VT
 * block, 733/739 are TX ZIPs inside the OK block, 75502 is Texarkana AR).
 * Prefixes not claimed by any state (territories, military APO/FPO, unassigned)
 * are intentionally absent — see zipMatchesUsState's fail-open rule.
 */
const US_STATE_ZIP_PREFIX_RANGES: Record<string, PrefixRange[]> = {
  AL: [[350, 369]],
  AK: [[995, 999]],
  AZ: [[850, 865]],
  AR: [[716, 729], [755, 755]],
  CA: [[900, 961]],
  CO: [[800, 816]],
  CT: [[60, 69]],
  DC: [[200, 200], [202, 205], [569, 569]],
  DE: [[197, 199]],
  FL: [[320, 339], [341, 349]],
  GA: [[300, 319], [398, 399]],
  HI: [[967, 968]],
  ID: [[832, 838]],
  IL: [[600, 629]],
  IN: [[460, 479]],
  IA: [[500, 528]],
  KS: [[660, 679]],
  KY: [[400, 427]],
  LA: [[700, 714]],
  ME: [[39, 49]],
  MD: [[206, 219]],
  MA: [[10, 27], [55, 55]],
  MI: [[480, 499]],
  MN: [[550, 567]],
  MS: [[386, 397]],
  MO: [[630, 658]],
  MT: [[590, 599]],
  NE: [[680, 693]],
  NV: [[889, 898]],
  NH: [[30, 38]],
  NJ: [[70, 89]],
  NM: [[870, 884]],
  NY: [[4, 5], [63, 63], [100, 149]],
  NC: [[270, 289]],
  ND: [[580, 588]],
  OH: [[430, 459]],
  OK: [[730, 749]],
  OR: [[970, 979]],
  PA: [[150, 196]],
  RI: [[28, 29]],
  SC: [[290, 299]],
  SD: [[570, 577]],
  TN: [[370, 385]],
  TX: [[733, 733], [739, 739], [750, 799], [885, 885]],
  UT: [[840, 847]],
  VT: [[50, 59]],
  VA: [[201, 201], [220, 246]],
  WA: [[980, 994]],
  WV: [[247, 268]],
  WI: [[530, 549]],
  WY: [[820, 831]],
};

/**
 * Individual ZIPs that straddle a state line: USPS assigns the prefix to one
 * state, but residents on the other side legally live (and must verify KYC)
 * in the neighboring one. Maps ZIP → additional states to accept on top of
 * the prefix table.
 */
const MULTI_STATE_ZIP_EXCEPTIONS: Record<string, string[]> = {
  '02861': ['MA'], // Pawtucket RI, Attleboro MA side
  '30165': ['AL'], // Rome GA, Cherokee County AL side
  '38079': ['KY'], // Tiptonville TN, Kentucky Bend
  '42223': ['TN'], // Fort Campbell (KY prefix, TN side)
  '57078': ['NE'], // Yankton SD, NE side
  '59221': ['ND'], // Fairview MT, ND side
  '63673': ['IL'], // Saint Mary MO, Kaskaskia Island IL
  '71749': ['LA'], // Junction City AR, LA side
  '81137': ['NM'], // Ignacio CO, NM side
  '83414': ['WY'], // Alta WY, served through Driggs ID
  '84536': ['AZ'], // Monument Valley UT, AZ side
  '86044': ['UT'], // Tonalea AZ, Navajo Mountain UT side
  '86514': ['UT'], // Teec Nos Pos AZ, UT side
  '86515': ['NM'], // Window Rock AZ, NM side
  '88063': ['TX'], // Sunland Park NM, El Paso County TX side
  '89060': ['CA'], // Pahrump NV, Charleston View CA
  '89439': ['CA'], // Verdi NV, CA side
  '97635': ['CA'], // New Pine Creek OR, CA side
};

const ALL_RANGES: PrefixRange[] = Object.values(US_STATE_ZIP_PREFIX_RANGES).flat();

const inRanges = (prefix: number, ranges: PrefixRange[]): boolean =>
  ranges.some(([start, end]) => prefix >= start && prefix <= end);

/**
 * Checks that a US ZIP code is consistent with the selected state.
 *
 * Fails open (returns true) when the check does not apply, so it only ever
 * flags combinations we positively know are wrong:
 * - zip is not a well-formed 5-digit US ZIP (format errors are reported by
 *   the dedicated zip format rule, not this one)
 * - state is not a known US state/DC code (reported by the state rule)
 * - the ZIP prefix is not claimed by any state (territories, APO/FPO,
 *   unassigned ranges)
 */
export function zipMatchesUsState(zip: string, state: string): boolean {
  // Fail open rather than throw if a future caller passes non-strings
  if (typeof zip !== 'string' || typeof state !== 'string') {
    return true;
  }

  if (!USA_ZIP_CODE_REGEX.test(zip)) {
    return true;
  }

  const stateCode = state.toUpperCase();
  if (MULTI_STATE_ZIP_EXCEPTIONS[zip]?.includes(stateCode)) {
    return true;
  }

  const ranges = US_STATE_ZIP_PREFIX_RANGES[stateCode];
  if (!ranges) {
    return true;
  }

  const prefix = parseInt(zip.slice(0, 3), 10);
  if (inRanges(prefix, ranges)) {
    return true;
  }

  return !inRanges(prefix, ALL_RANGES);
}
